import { create } from "zustand";

import {
  confirmSignIn,
  confirmSignUp,
  fetchAuthSession,
  signIn,
  signOut,
} from "aws-amplify/auth";

import { configureAmplify } from "./amplify";
import { checkLng } from "@/utils";
import { toaster } from "@/components/ui/toaster";

// Configure Amplify on store initialization
configureAmplify();

const usePersistentStore = create((set, get) => {
  const initialState = {
    isAuthenticated: false,
    cognitoId: "",
    idToken: {},
    jwtIdToken: "",
    groups: [],
    activeGroup: "",
    isSuper: false,
    isAdmin: [],
    loginName: "",
    knowledgebaseId: "",
    language: checkLng(),
    env:
      typeof window !== "undefined"
        ? window.location.hostname === "localhost" &&
          !process.env.NEXT_PUBLIC_ENV
          ? "dev"
          : process.env.NEXT_PUBLIC_ENV || "dev"
        : "dev",
    socketUpdate: {},
    connectionId: "",
    verifiedEmail: "",
    userStatus: null,
    queueStatus: undefined,
    setLoginName: (name) => {
      set({ loginName: name });
    },
    setKnowledgebaseId: (id) => {
      set({ knowledgebaseId: id });
    },
    getJWTIdToken: () => {
      return get().jwtIdToken;
    },
    getActiveGroup: () => {
      if (typeof window !== "undefined") {
        return (
          localStorage.getItem("prifina-base-network") || get().activeGroup
        );
      }
      return get().activeGroup;
    },
    setActiveGroup: (group) => {
      if (typeof window !== "undefined") {
        localStorage.setItem("prifina-base-network", group);
      }
      set({ activeGroup: group });
    },
    signIn: async (username) => {
      try {
        const { nextStep } = await signIn({
          username,
          options: {
            authFlowType: "CUSTOM_WITHOUT_SRP",
          },
        });

        // should be this signInStep ==="CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE"
        if (nextStep.signInStep === "CONFIRM_SIGN_IN_WITH_CUSTOM_CHALLENGE") {
          return Promise.resolve(true);
        }
      } catch (error) {
        await signOut();
      }

      return Promise.resolve(false);
    },
    confirmSignIn: async (code, loginType = null) => {
      try {
        // to send the answer of the custom challenge
        const { nextStep } = await confirmSignIn({
          challengeResponse: code,
          options: {
            clientMetadata: {
              flow: "base-otp",
              loginName: get().loginName,
              loginType: loginType ? String(loginType) : null,

              // any non-sensitive routing hint
            },
          },
        });
        // console.log(nextStep, rest);
        //{isSignedIn: true,nextStep: {signInStep: "DONE"}}
        if (nextStep.signInStep === "DONE") {
          // set({ isAuthenticated: true });
          await get().isLoggedIn();
          return Promise.resolve(true);
        }
      } catch (err) {
        // Error during sign in confirmation
      }
      return Promise.resolve(false);
    },
    isLoggedIn: async () => {
      try {
        // const session = await currentSession()
        const { tokens } = await fetchAuthSession({ forceRefresh: true });
        if (!tokens) {
          set({
            isAuthenticated: false,
            idToken: "",
            cognitoId: "",
            knowledgebaseId: "",
            loginName: "",
            verifiedEmail: "",
          });
          // set({ user: initUser });
          return Promise.resolve(false);
        }
        const idToken = tokens.idToken.payload;
        let activeGroup = get().getActiveGroup();
        //console.log("STORE ACTIVE GROUP START", activeGroup);

        const isSuper = idToken?.["cognito:groups"]?.includes("super") || false;
        const isAdmin =
          idToken?.["cognito:groups"]?.filter((m) => m.startsWith("admin_")) ||
          [];
        
        // Check if user has admin or super permissions
        if (!isSuper && isAdmin.length === 0) {
          toaster.create({
            title: "Access Denied",
            description: "You don't have permission to access this admin application.",
            type: "error",
            duration: 5000,
          });
          await signOut();
          return Promise.resolve(false);
        }
        
        if (
          activeGroup !== "" &&
          !(idToken?.["cognito:groups"]?.includes(activeGroup) || false)
        ) {
          activeGroup = isSuper
            ? "super"
            : isAdmin.length > 0
            ? isAdmin[0]
            : "";
        }
        if (activeGroup === "") {
          activeGroup = isSuper
            ? "super"
            : isAdmin.length > 0
            ? isAdmin[0]
            : "";
        }

        set({
          isSuper,
          isAdmin,
          verifiedEmail: idToken.email || "",
          cognitoId: idToken["cognito:username"],
          knowledgebaseId: idToken["custom:knowledgebaseId"] || "",
          //isAuthenticated: authStatus,
          //idToken: idToken,
          //jwtIdToken,
          groups: idToken?.["cognito:groups"] || [],
          activeGroup,
        });
        /*   const currentUser = {
            id: idToken["cognito:username"],
            email: idToken["email"],
            firstName: idToken["given_name"],
            lastName: idToken["family_name"],
            username: idToken["preferred_username"],
            email_verified: idToken["email_verified"],
          };
          const prevUser = get().user;
          set({ user: { ...prevUser, ...currentUser } });
          await get().setCSFRToken(); */
        return Promise.resolve(true);
      } catch (e) {
        if (typeof e === "string" && e === "No current user") {
          return Promise.resolve(false);
        }
        return Promise.resolve(false);
      }
    },
    usernameAvailable: async (username) => {
      /* 
    Uncheck this in cognito app client integration...
    Prevent user existence errors
    Info
    Amazon Cognito authentication APIs return a generic authentication failure response, indicating either the user name or password is incorrect, instead of indicating that the user was not found.
    */
      // adapted from @herri16's solution: https://github.com/aws-amplify/amplify-js/issues/1067#issuecomment-436492775
      /* await confirmSignUp({
          username,
          confirmationCode
        });
       */ /* 
                                                                                                                                                                                  , {
                                                                                                                                                                                    // If set to False, the API will throw an AliasExistsException error if the phone number/email used already exists as an alias with a different user
                                                                                                                                                                                    // forceAliasCreation: false
                                                                                                                                                                                  } */
      try {
        await confirmSignUp({
          username,
          confirmationCode: "000000",
          forceAliasCreation: false,
        });
        // this should always throw an error of some kind, but if for some reason this succeeds then the user probably exists.
        return false;
      } catch (err) {
        /*  console.log(err);
             console.log(err.message);
             console.log(err.code);
             console.log(Object.keys(err));
             console.log(err.name)
      */
        switch (err.name) {
          case "UserNotFoundException":
            return true;
          case "NotAuthorizedException":
            return false;
          case "AliasExistsException":
            // Email alias already exists
            return false;
          case "CodeMismatchException":
            return false;
          case "ExpiredCodeException":
            return false;
          default:
            return false;
        }
      }
    },
    setQueueStatus: (obj) => {
      set(() => ({ queueStatus: obj }));
    },
    setUserStatus: (status) => {
      set(() => ({ userStatus: status }));
    },
    setConnectionId: (str) => {
      set(() => ({ connectionId: str }));
    },

    setSocketUpdate: (obj) => {
      set(() => ({ socketUpdate: obj }));
    },
  };

  // Initialize state with sessionStorage or initialState
  let savedState = initialState;
  if (typeof window !== "undefined") {
    const stored = sessionStorage.getItem("prifina-base");
    if (stored) {
      savedState = { ...initialState, ...JSON.parse(stored) };
    }
  }

  return savedState;
});

// Subscribe to store changes to save to sessionStorage
if (typeof window !== "undefined") {
  usePersistentStore.subscribe((state) => {
    // eslint-disable-next-line no-unused-vars
    const {
      signIn: _signIn,
      confirmSignIn: _confirmSignIn,
      isLoggedIn: _isLoggedIn,
      usernameAvailable: _usernameAvailable,
      setLoginName: _setLoginName,
      getJWTIdToken: _getJWTIdToken,
      getActiveGroup: _getActiveGroup,
      setActiveGroup: _setActiveGroup,
      setKnowledgebaseId: _setKnowledgebaseId,
      setSocketUpdate: _setSocketUpdate,
      setConnectionId: _setConnectionId,
      setQueueStatus: _setQueueStatus,
      setUserStatus: _setUserStatus,
      ...stateToSave
    } = state;
    sessionStorage.setItem("prifina-base", JSON.stringify(stateToSave));
  });
}

export default usePersistentStore;
